import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { RefreshCw, Link2, Unlink } from 'lucide-react';
import GlassCard from '@/components/ui/glass-card';

interface WearableProvider {
  id: string;
  name: string;
  icon: string;
  color: string;
  isConnected: boolean;
  lastSync: string | null;
  dataTypes: string[];
}

const providers: WearableProvider[] = [
  {
    id: 'apple-health',
    name: 'Apple Health',
    icon: '🍎',
    color: '#FF2D55',
    isConnected: true,
    lastSync: '2 hours ago',
    dataTypes: ['Steps', 'Heart Rate', 'Sleep', 'Workouts'],
  },
  {
    id: 'fitbit',
    name: 'Fitbit',
    icon: '⌚',
    color: '#00B0B9',
    isConnected: true,
    lastSync: '30 minutes ago',
    dataTypes: ['Steps', 'Heart Rate', 'Sleep', 'SpO2'],
  },
  {
    id: 'garmin',
    name: 'Garmin',
    icon: '🏃',
    color: '#007CC3',
    isConnected: false,
    lastSync: null,
    dataTypes: ['Steps', 'Heart Rate', 'GPS', 'Training'],
  },
  {
    id: 'oura',
    name: 'Oura Ring',
    icon: '💍',
    color: '#C4B09C',
    isConnected: false,
    lastSync: null,
    dataTypes: ['Sleep', 'Readiness', 'Activity', 'Temperature'],
  },
  {
    id: 'samsung',
    name: 'Samsung Health',
    icon: '📱',
    color: '#1428A0',
    isConnected: false,
    lastSync: null,
    dataTypes: ['Steps', 'Heart Rate', 'Blood Pressure', 'ECG'],
  },
  {
    id: 'google-fit',
    name: 'Google Fit',
    icon: '❤️',
    color: '#4285F4',
    isConnected: false,
    lastSync: null,
    dataTypes: ['Steps', 'Heart Rate', 'Workouts', 'Nutrition'],
  },
];

const WearableConnections: React.FC = () => {
  const [providerStates, setProviderStates] = useState<WearableProvider[]>(providers);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const handleConnect = (id: string) => {
    setProviderStates(prev =>
      prev.map(p =>
        p.id === id
          ? { ...p, isConnected: true, lastSync: 'Just now' }
          : p
      )
    );
  };

  const handleDisconnect = (id: string) => {
    setProviderStates(prev =>
      prev.map(p =>
        p.id === id
          ? { ...p, isConnected: false, lastSync: null }
          : p
      )
    );
  };

  const handleSync = async (id: string) => {
    setSyncingId(id);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setProviderStates(prev =>
      prev.map(p =>
        p.id === id
          ? { ...p, lastSync: 'Just now' }
          : p
      )
    );
    setSyncingId(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {providerStates.map((provider) => (
        <GlassCard key={provider.id} className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div
                className="text-3xl w-12 h-12 flex items-center justify-center rounded-lg"
                style={{ backgroundColor: `${provider.color}15` }}
              >
                {provider.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {provider.name}
                </h3>
                <div className="mt-1">
                  {provider.isConnected ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 dark:bg-green-400" />
                      Connected
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {provider.isConnected ? (
            <>
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Last sync:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {provider.lastSync}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {provider.dataTypes.map((type) => (
                    <span
                      key={type}
                      className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSync(provider.id)}
                  disabled={syncingId === provider.id}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                    'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <RefreshCw
                    className={cn(
                      'w-4 h-4',
                      syncingId === provider.id && 'animate-spin'
                    )}
                  />
                  <span className="text-sm">
                    {syncingId === provider.id ? 'Syncing...' : 'Sync Now'}
                  </span>
                </button>
                <button
                  onClick={() => handleDisconnect(provider.id)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <Unlink className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Available data:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {provider.dataTypes.map((type) => (
                    <span
                      key={type}
                      className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>

              <button
                onClick={() => handleConnect(provider.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Link2 className="w-4 h-4" />
                <span className="text-sm">Connect</span>
              </button>
            </>
          )}
        </GlassCard>
      ))}
    </div>
  );
};

export default WearableConnections;
